/* predictive-search.js — live suggestions for every search box: the always-on
   header bar, the search drawer (the header icon, and phones on product
   pages) and the box on the results page. Adds: rotating "typewriter"
   placeholder, recent + popular searches while the box is empty, matching
   collections, and typo/half-word rescue via Shopify's own suggestions. */
(function () {
  'use strict';
  var theme = window.theme || {};
  // Search suggestions show the variant's own price and compare-at, nothing
  // computed. This used to derive a discount from theme.sale when the sale was
  // store-wide — the predictive-search JSON carries no collection data, so a
  // scoped sale had to be skipped here anyway. Since 2026-08-14 the sale is real
  // compare-at pricing, so the JSON already contains both numbers and there is
  // nothing to infer.

  // Americana prompts for the typewriter placeholder.
  var TERMS = ['Civil War', 'George Washington', 'Cowboys', 'Bald eagle', 'American flag',
    'Abraham Lincoln', 'The Revolution', 'Route 66', 'Statue of Liberty', 'Gettysburg'];
  // Shown under "Popular" while the box is empty. Same list as the results
  // page's Trending chips (templates/search.json) — keep the two in step.
  var POPULAR = (theme.search && theme.search.popular) || ['Lincoln', 'Flag', 'Civil War', 'Washington', 'Eagle'];

  function str(key, fallback) { return (theme.strings && theme.strings[key]) || fallback; }
  // Quotes too: the output also goes into href and src attributes.
  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  // "%n pieces" -> "6 pieces"; split/join so a "$" in the text is never a replace() pattern.
  function counted(n, one, many) { return (n === 1 ? one : many).split('%n').join(String(n)); }
  function scope() { return theme.searchScope || window.theme.searchScope; }
  function resultsUrl(term) {
    var sc = scope();
    return sc ? sc.url(term) : ((theme.routes && theme.routes.search) || '/search') + '?q=' + encodeURIComponent(term) + '&type=product';
  }

  // Whole-dollar prices print without ".00", like the rest of the storefront.
  function money(amount) {
    var fmt = theme.moneyFormat || '${{amount}}';
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, esc(String(amount).replace(/\.00$/, '')));
  }
  function num(s) { return parseFloat(String(s == null ? '' : s).replace(/[^0-9.]/g, '')) || 0; }

  // The struck price must belong to the same variant as the price beside it.
  // The product-level compare_at_price_max paired with the cheapest price put
  // "$89 ~~$535~~" on a sale ladder, so the pair comes from the cheapest
  // variant in the payload; with no variants there is no comparison at all.
  function priceHtml(p) {
    if (typeof p.price !== 'string' || !p.price) return '';
    var now = p.price, compare = null;
    if (p.variants && p.variants.length) {
      var cheapest = p.variants.reduce(function (a, v) { return !a || num(v.price) < num(a.price) ? v : a; }, null);
      now = cheapest.price;
      if (num(cheapest.compare_at_price) > num(cheapest.price)) compare = cheapest.compare_at_price;
    } else if (num(p.compare_at_price_min) > num(p.price)) {
      // The suggest payload usually carries no variants. On the size ladder the
      // cheapest size also has the lowest compare-at, so the two minimums are
      // one variant's pair (never compare_at_price_max, which is the largest).
      compare = p.compare_at_price_min;
    }
    if (compare) {
      return '<span class="ps-item__price ps-item__price--sale"><span class="ps-item__now">' + money(now) +
        '</span> <s class="ps-item__was">' + money(compare) + '</s></span>';
    }
    return '<span class="ps-item__price">' + money(now) + '</span>';
  }

  // The suggest JSON hands back the full-resolution upload (often 3000px+),
  // which took seconds to arrive and left the 48px tile as a blank navy
  // square. Ask the CDN for a 96px rendition and load it eagerly — the
  // dropdown is only on screen while the shopper is looking at it.
  function thumb(src) {
    if (!src) return '<span class="ps-item__ph"></span>';
    src += (src.indexOf('?') > -1 ? '&' : '?') + 'width=96';
    return '<img src="' + esc(src) + '" alt="" width="48" height="48" loading="eager" decoding="async">';
  }

  function chips(list, cls) {
    return list.map(function (t) {
      return '<a class="ps-chip' + (cls ? ' ' + cls : '') + '" href="' + esc(resultsUrl(t)) + '">' + esc(t) + '</a>';
    }).join('');
  }

  /* Empty box: recent searches (this browser) and the popular list.
     render() and renderIdle() return the line the status region announces
     (nothing for the idle list). */
  function renderIdle(container) {
    var sc = scope();
    var recent = sc ? sc.recent.get() : [];
    var html = '';
    if (recent.length) {
      html += '<div class="ps-group"><div class="ps-group__head"><span class="ps-heading">' + esc(str('recentSearches', 'Recent searches')) +
        '</span><button type="button" class="ps-clear" data-ps-clear>' + esc(str('clear', 'Clear')) + '</button></div>' +
        '<div class="ps-chips">' + chips(recent, 'ps-chip--recent') + '</div></div>';
    }
    html += '<div class="ps-group"><div class="ps-group__head"><span class="ps-heading">' + esc(str('popularSearches', 'Popular')) +
      '</span></div><div class="ps-chips">' + chips(POPULAR) + '</div></div>';
    container.innerHTML = html;
    container.hidden = false;
    return '';
  }

  /* One short line for screen readers ("6 pieces, 2 collections", "No exact
     matches. 6 closest matches. Did you mean lincoln?") instead of a live
     list that re-read every title and price on each keystroke. */
  function summary(res, products, colls, fixes) {
    var bits = [];
    if (products.length) {
      bits.push(res.fallback
        ? counted(products.length, str('closestMatchOne', '%n closest match'), str('closestMatchMany', '%n closest matches'))
        : counted(products.length, str('pieceOne', '%n piece'), str('pieceMany', '%n pieces')));
    }
    if (colls.length) bits.push(counted(colls.length, str('collectionOne', '%n collection'), str('collectionMany', '%n collections')));
    if (!bits.length && !fixes.length) return str('noResults', 'No matches yet');
    var out = bits.join(', ');
    if (res.fallback) out = str('noExactMatches', 'No exact matches') + (out ? '. ' + out : '');
    if (fixes.length) out += (out ? '. ' : '') + str('didYouMean', 'Did you mean') + ' ' + fixes[0] + '?';
    return out;
  }

  function render(container, q, res) {
    var html = '';
    // Shopify's corrections / completions ("linclon" -> lincoln, "gettys" ->
    // gettysburg). Only asked for when the exact words found no products.
    var fixes = (res.queries || []).map(function (x) { return x.text; })
      .filter(function (t) { return t && t.toLowerCase() !== q.toLowerCase(); }).slice(0, 3);
    if (fixes.length) {
      html += '<div class="ps-group ps-group--fixes"><span class="ps-heading">' + esc(str('didYouMean', 'Did you mean')) +
        '</span><div class="ps-chips">' + chips(fixes, 'ps-chip--fix') + '</div></div>';
    }
    var colls = (res.collections || []).slice(0, 3);
    if (colls.length) {
      html += '<div class="ps-group"><span class="ps-heading">' + esc(str('collections', 'Collections')) + '</span><ul class="ps-colls" role="list">';
      colls.forEach(function (c) {
        var img = c.featured_image && c.featured_image.url;
        html += '<li><a class="ps-coll" href="' + esc(c.url) + '"><span class="ps-coll__media">' + thumb(img) + '</span>' +
          '<span class="ps-coll__title">' + esc(c.title) + '</span><span class="ps-coll__arrow" aria-hidden="true">&rarr;</span></a></li>';
      });
      html += '</ul></div>';
    }
    var products = (res.products || []).slice(0, 6);
    if (products.length) {
      html += '<div class="ps-group"><span class="ps-heading">' + esc(res.fallback ? str('closestMatches', 'Closest matches') : str('products', 'Pieces')) +
        '</span><ul class="ps-list" role="list">';
      products.forEach(function (p) {
        html += '<li><a class="ps-item" href="' + esc(p.url) + '"><span class="ps-item__media">' + thumb(p.image || (p.featured_image && p.featured_image.url)) + '</span>' +
          '<span class="ps-item__title">' + esc(p.title) + '</span>' + priceHtml(p) + '</a></li>';
      });
      html += '</ul></div>';
    }
    if (!products.length && !colls.length && !fixes.length) {
      html += '<p class="ps-empty">' + esc(str('noResults', 'No matches yet')) + '</p>' +
        '<div class="ps-group"><span class="ps-heading">' + esc(str('popularSearches', 'Popular')) + '</span><div class="ps-chips">' + chips(POPULAR) + '</div></div>';
    }
    // The same scoped result set the form submits, in the shopper's own words.
    // When only Shopify's fuzzy pass found anything, follow its correction
    // (the results page then says "Showing results for …"). Without one, the
    // results page finds its own correction or shows the wide matches.
    if (!res.fallback || fixes.length || products.length) {
      var allTerm = res.fallback && fixes.length ? fixes[0] : q;
      var allHref = resultsUrl(allTerm) + (allTerm !== q ? '&corrected_from=' + encodeURIComponent(q) : '');
      html += '<a class="ps-all" href="' + esc(allHref) + '">' + esc(str('seeAll', 'See all results for')) + ' “' + esc(allTerm) + '” &rarr;</a>';
    }
    container.innerHTML = html;
    container.hidden = false;
    return summary(res, products, colls, fixes);
  }

  /* ---- fetching ---- */
  // The cache lives in search-scope.js, shared with the submit-time spelling
  // correction there.
  function suggest(q, types, signal) {
    var sc = scope();
    if (sc && sc.suggest) return sc.suggest(q, types, signal);
    return Promise.resolve({ products: [], collections: [], queries: [] });
  }

  /* Two passes, the second only when the first finds no products:
       1. the scoped words — the same rules the results page uses, so the
          dropdown never shows a print that merely mentions the word in its
          description, and "See all" lands on these same pieces;
       2. the raw text, which Shopify matches with typo and prefix tolerance
          ("linclon", "gettys", "eag"), plus its suggested corrections.
     Field-scoped terms get neither tolerance, so mid-word typing lands in
     pass 2 until the word is complete. */
  function fetchScoped(q, signal) {
    var sc = scope();
    var scoped = sc && !sc.isAlreadyScoped(q) ? sc.build(q) : '';
    var first = scoped ? suggest(scoped, 'product,collection', signal) : Promise.resolve(null);
    return first.then(function (r1) {
      if (r1 && r1.products.length) return { products: r1.products, collections: r1.collections, fallback: false };
      return suggest(q, 'query,product,collection', signal).then(function (r2) {
        return {
          products: r2.products,
          collections: (r1 && r1.collections.length) ? r1.collections : r2.collections,
          queries: r2.queries,
          fallback: true
        };
      });
    });
  }

  /* Keep the list above the phone keyboard: the visual viewport shrinks when
     it opens while vh units do not, so 70vh ran under the keys. */
  function fit(container) {
    if (container.hidden) return;
    var vv = window.visualViewport;
    var bottom = vv ? vv.height + vv.offsetTop : window.innerHeight;
    var room = Math.floor(bottom - container.getBoundingClientRect().top - 12);
    container.style.maxHeight = Math.max(180, Math.min(room, 560)) + 'px';
  }

  var uid = 0;
  function bind(input) {
    var scopeEl = input.closest('[data-predictive-scope]') || document;
    var results = scopeEl.querySelector('[data-predictive-results]');
    if (!results) return;
    var timer, seq = 0, ctrl = null;
    if (!input.getAttribute('enterkeyhint')) input.setAttribute('enterkeyhint', 'search');

    /* Combobox semantics (the markup sets role, aria-autocomplete and
       aria-controls): aria-expanded follows the list's hidden attribute,
       whoever flips it (Escape, outside click, scroll, a render). */
    if (!results.id) results.id = 'PredictiveResults-' + (++uid);
    input.setAttribute('aria-controls', results.id);
    function syncExpanded() { input.setAttribute('aria-expanded', results.hidden ? 'false' : 'true'); }
    syncExpanded();
    if (window.MutationObserver) new MutationObserver(syncExpanded).observe(results, { attributes: true, attributeFilter: ['hidden'] });

    // Polite status line, next to the list (not inside it: the list is rewritten on every render).
    var status = document.createElement('p');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    results.parentNode.insertBefore(status, results.nextSibling);
    // Unchanged text is left alone, so "6 pieces" isn't repeated on every keystroke.
    function announce(text) { if (status.textContent !== text) status.textContent = text; }

    function show(fn) { announce(fn() || ''); fit(results); }

    function search() {
      var q = input.value.trim();
      if (ctrl) { ctrl.abort(); ctrl = null; }
      if (q.length < 2) { seq++; input.removeAttribute('data-no-exact'); show(function () { return renderIdle(results); }); return; }
      var mine = ++seq;
      ctrl = window.AbortController ? new AbortController() : null;
      fetchScoped(q, ctrl && ctrl.signal).then(function (res) {
        // A slow answer for "ea" must not replace the one for "eagle".
        if (mine !== seq || input.value.trim() !== q) return;
        // These exact words find nothing: search-scope.js corrects on submit.
        if (res.fallback) input.setAttribute('data-no-exact', q.toLowerCase());
        else input.removeAttribute('data-no-exact');
        show(function () { return render(results, q, res); });
      }).catch(function () {});
    }

    input.addEventListener('input', function () { clearTimeout(timer); timer = setTimeout(search, 200); });
    // Set by Escape inside the list: focus goes back to the box and the list
    // stays closed (the focus handler used to reopen it straight away).
    var quietFocus = false;
    input.addEventListener('focus', function () {
      if (quietFocus) { quietFocus = false; return; }
      if (!results.hidden && results.innerHTML) return;
      search();
    });
    // Text typed before this script ran (the bar is on screen and typeable
    // well before deferred scripts on a phone) never fired an input event we
    // heard: look it up now.
    if (document.activeElement === input) search();

    results.addEventListener('click', function (e) {
      var clear = e.target.closest('[data-ps-clear]');
      if (!clear) return;
      e.preventDefault();
      var sc = scope();
      if (sc) sc.recent.clear();
      show(function () { return renderIdle(results); });
      input.focus();
    });

    /* Close the dropdown on outside click / Escape. The event path, not
       contains(): "Clear" re-renders the list, so by the time the click
       reaches the document its button is detached and contains() said
       "outside", closing the whole list. The path is fixed at dispatch. */
    document.addEventListener('click', function (e) {
      var path = e.composedPath ? e.composedPath() : null;
      var inside = path && path.length ? path.indexOf(scopeEl) !== -1 : scopeEl.contains(e.target);
      if (!inside) results.hidden = true;
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { results.hidden = true; return; }
      if (e.key === 'ArrowDown' && !results.hidden) {
        var first = results.querySelector('a');
        if (first) { e.preventDefault(); first.focus(); }
      }
    });
    results.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Escape') return;
      var links = Array.prototype.slice.call(results.querySelectorAll('a'));
      var i = links.indexOf(document.activeElement);
      e.preventDefault();
      if (e.key === 'Escape') { results.hidden = true; quietFocus = true; input.focus(); quietFocus = false; return; }
      var next = e.key === 'ArrowDown' ? i + 1 : i - 1;
      if (next < 0) input.focus();
      else if (links[next]) links[next].focus();
    });

    if (window.visualViewport) window.visualViewport.addEventListener('resize', function () { fit(results); });

    // A touch scroll of the page fires no click, so the "outside click" rule
    // never closed the list and the sticky header (which stays put while it is
    // open, see global.js) followed the shopper down the page. Close it once
    // the page has moved well away; small jumps from the keyboard opening or
    // closing leave it alone.
    var openedAt = null;
    input.addEventListener('focus', function () { openedAt = window.scrollY; });
    window.addEventListener('scroll', function () {
      if (results.hidden || document.activeElement === input || results.contains(document.activeElement)) { openedAt = window.scrollY; return; }
      if (openedAt === null) openedAt = window.scrollY;
      if (Math.abs(window.scrollY - openedAt) > 120) results.hidden = true;
    }, { passive: true });
  }

  /* ---- rotating "typewriter" placeholder ---- */
  function typewriter(input) {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { input.placeholder = 'Search “' + TERMS[0] + '”'; return; }
    // One pass through TERMS, then stop: an endless setTimeout chain kept a
    // timer (and a placeholder repaint) alive for the whole session on every
    // page. It also idles while the tab is hidden.
    var ti = 0, ci = 0, deleting = false;
    function tick() {
      // Pause while the field is in use, or while the tab is in the background.
      if (document.hidden || document.activeElement === input || input.value) { return setTimeout(tick, 1200); }
      var term = TERMS[ti];
      if (!deleting) {
        ci++;
        input.placeholder = 'Search “' + term.slice(0, ci) + '”';
        if (ci >= term.length) { deleting = true; return setTimeout(tick, 1500); }
        return setTimeout(tick, 85);
      }
      ci--;
      input.placeholder = 'Search “' + term.slice(0, Math.max(0, ci)) + '”';
      if (ci <= 0) {
        deleting = false;
        ti++;
        // Last term typed and cleared — leave the placeholder on it and stop.
        if (ti >= TERMS.length) { input.placeholder = 'Search “' + TERMS[TERMS.length - 1] + '”'; return; }
        return setTimeout(tick, 300);
      }
      return setTimeout(tick, 40);
    }
    tick();
  }

  function init() {
    document.querySelectorAll('[data-predictive-input]').forEach(function (input) {
      bind(input);
      if (input.hasAttribute('data-typewriter')) typewriter(input);
    });

    // Header search icon (only present when the always-on bar is disabled) → open drawer
    var drawer = document.getElementById('search-drawer');
    document.addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-search-toggle]');
      if (toggle && drawer && theme.drawers) {
        e.preventDefault();
        theme.drawers.open('search-drawer');
        var di = drawer.querySelector('[data-predictive-input]');
        if (di) setTimeout(function () { di.focus(); }, 90);
      }
    });
  }

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); }
  else { init(); }
})();
